/**
 * Route admission guard.
 *
 * Attached to each protected route as `beforeEnter` in routes.js.
 *
 * WHY NOT AuthGuard.svelte
 * ------------------------
 * The portal originally wrapped protected routes in an <AuthGuard> component
 * rendered from a `{#snippet fallback(route)}`. That never ran: `fallback` is
 * not a prop of @dvcol/svelte-simple-router@1.11.2 — its snippets are
 * `routing`, `loading`, `error` and `children` — so the snippet was silently
 * ignored and `meta.requiresRole` was decorative from the day it was written.
 *
 * WHY beforeEnter AND NOT router.beforeEach
 * -----------------------------------------
 * Both are real APIs. `beforeEnter` is per-route and declarative, so it needs
 * no handle on the Router instance — and therefore requires no change to
 * App.svelte's RouterContext/RouterView wiring, which is fragile and hard-won
 * (see the comment block there). A global beforeEach would have meant reaching
 * for the router instance and re-opening exactly that.
 *
 * GUARD CONTRACT (from the library's own types)
 *   return an object with `name` or `path` -> redirect (followGuardRedirects
 *                                             defaults to true)
 *   return false / an Error / throw       -> abort the navigation
 *   return true or undefined              -> admit
 *
 * THIS IS UX, NOT SECURITY. Every corresponding endpoint re-derives scope from
 * the session server-side and 403s independently — verified. The guard exists
 * so a user without the role gets sent somewhere useful instead of watching a
 * screen load and then error.
 */

import { authStore } from '../stores/authStore.svelte.js';

/** Roles are declared as a string or an array of strings. */
function allows(requiresRole, role) {
  if (!requiresRole) return true;
  return Array.isArray(requiresRole)
    ? requiresRole.includes(role)
    : requiresRole === role;
}

/**
 * @param {object} navigation  the library's INavigationEvent
 * @returns {Promise<true | {name: string}>}
 */
export async function guardRoute(navigation) {
  const meta = navigation?.to?.route?.meta ?? {};

  if (!meta.requiresAuth) return true;

  if (!authStore.isAuthenticated) return { name: 'login' };

  // Awaited on purpose. Guards may return a promise, and on a cold load onto a
  // role-gated URL the context has not arrived yet — deciding synchronously
  // would read role === null and bounce a legitimate super-user.
  try {
    await authStore.ensureContext();
  } catch (e) {
    // 403 here means an authenticated chassis account with no portal
    // membership. Sign out rather than looping on a screen they cannot use.
    if (e?.status === 403) {
      authStore.clearAuth();
      return { name: 'login' };
    }
    // Anything else (network, 500) is not an authorization answer. Admit and
    // let the screen surface the real error — bouncing to the dashboard here
    // would disguise an outage as a permissions problem.
    return true;
  }

  // ensureContext resolves null when the token expired between the check above
  // and now.
  if (!authStore.role) return { name: 'login' };

  if (!allows(meta.requiresRole, authStore.role)) {
    // Dashboard rather than login: they ARE signed in, just not entitled to
    // this screen. Sending them to login would read as a session problem.
    return { name: 'dashboard' };
  }

  return true;
}
